import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from '@/components/ui';
import { Badge } from '@/components/ui';
import { cn } from '@/components/ui';
import { useLLMConfigApi, type LLMConfigApi } from '../../services/llm-config-api';
import type { LLMConfig, ListLLMConfigsParams } from '../../types/llm-config';
import { AppConfigProvider } from '../../context/AppConfigContext';

// Internal component props (for use within WorkspacesComponent)
export interface LLMModelSelectInternalProps {
  value?: string;
  onValueChange?: (modelName: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  activeOnly?: boolean; // Only show active models
  provider?: string; // Filter by provider
  groupByProvider?: boolean; // Group models by provider
  showProviderBadge?: boolean; // Show provider badge in trigger
  /** Optional API instance for testing */
  llmApi?: LLMConfigApi;
}

// Standalone component props (includes context requirements)
export interface LLMModelSelectProps extends LLMModelSelectInternalProps {
  app_id: string;
  debug?: boolean;
}

// Internal component that uses context
export const LLMModelSelectInternal: React.FC<LLMModelSelectInternalProps> = ({
  value,
  onValueChange,
  placeholder = 'Select a model',
  disabled = false,
  className,
  activeOnly = true,
  provider,
  groupByProvider = false,
  showProviderBadge = false,
  llmApi: injectedApi,
}) => {
  const defaultApi = useLLMConfigApi();
  const llmApi = injectedApi ?? defaultApi;

  // Build query params
  const queryParams: ListLLMConfigsParams = {};
  if (activeOnly) {
    queryParams.activeOnly = true;
  }
  if (provider) {
    queryParams.provider = provider;
  }

  // Fetch LLM configs
  const { data: llmConfigs = [], isLoading } = useQuery<LLMConfig[]>({
    queryKey: ['llm-configs', queryParams],
    queryFn: () => llmApi.listLLMConfigs(queryParams),
    staleTime: 5 * 60_000,
  });

  // Group models by provider if requested
  const groupedModels = React.useMemo(() => {
    if (!groupByProvider) return null;

    const groups: Record<string, LLMConfig[]> = {};
    llmConfigs.forEach(model => {
      if (!groups[model.provider]) {
        groups[model.provider] = [];
      }
      groups[model.provider].push(model);
    });

    return groups;
  }, [llmConfigs, groupByProvider]);

  // Find selected model for displaying provider badge
  const selectedModel = React.useMemo(() => {
    return llmConfigs.find(model => model.modelName === value);
  }, [llmConfigs, value]);

  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled || isLoading}>
      <SelectTrigger className={cn('w-full', className)}>
        <SelectValue placeholder={isLoading ? 'Loading models...' : placeholder}>
          {selectedModel && (
            <div className="flex items-center gap-2">
              <span>{selectedModel.displayName}</span>
              {showProviderBadge && (
                <Badge variant="outline" className="text-xs">
                  {selectedModel.provider}
                </Badge>
              )}
            </div>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {isLoading ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            Loading models...
          </div>
        ) : llmConfigs.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            {activeOnly
              ? 'No active models available'
              : provider
              ? `No models found for ${provider}`
              : 'No models available'}
          </div>
        ) : groupedModels ? (
          // Grouped by provider
          Object.entries(groupedModels).map(([providerName, models]) => (
            <SelectGroup key={providerName}>
              <SelectLabel>{providerName}</SelectLabel>
              {models.map(model => (
                <SelectItem key={model.modelName} value={model.modelName}>
                  <div className="flex items-center justify-between w-full">
                    <span>{model.displayName}</span>
                    <span className="text-xs text-muted-foreground ml-2 font-mono">
                      {model.modelId}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectGroup>
          ))
        ) : (
          // Flat list
          llmConfigs.map(model => (
            <SelectItem key={model.modelName} value={model.modelName}>
              <div className="flex items-center gap-2">
                <span>{model.displayName}</span>
                <Badge variant="outline" className="text-xs">
                  {model.provider}
                </Badge>
                <span className="text-xs text-muted-foreground font-mono">
                  {model.modelId}
                </span>
              </div>
            </SelectItem>
          ))
        )}
      </SelectContent>
    </Select>
  );
};

// Standalone component that provides its own context
export const LLMModelSelect: React.FC<LLMModelSelectProps> = ({
  app_id,
  debug = false,
  ...props
}) => {
  return (
    <AppConfigProvider app_id={app_id} debug={debug}>
      <LLMModelSelectInternal {...props} />
    </AppConfigProvider>
  );
};
